import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Icon from './Icon';
import { company } from '../data/company';
import { productPages, productPagePath } from '../data/productPages';
import { masterBrands } from '../data/brands';
import { salesPhone, whatsappUrl, quotePath, directionsUrl, hoursLines } from '../utils/contactLinks';
import './Navigation.css';

// Top-level items; `children` render as a dropdown on desktop and a list in the mobile menu.
const MENU = [
  { label: 'Home', path: '/' },
  {
    label: 'Products',
    path: '/products',
    children: [
      ...productPages.map((p) => ({ label: p.name, path: productPagePath(p), note: p.summary })),
      { label: 'View all products →', path: '/products', all: true },
    ],
  },
  {
    label: 'Partners',
    path: '/partners',
    wide: true,
    children: [
      ...masterBrands.map((b) => ({
        label: b.shortName || b.name,
        path: `/partners#${b.id}`,
        note: 'Master distributor',
        logo: b.logo,
      })),
      { label: 'View all partners →', path: '/partners', all: true },
    ],
  },
  {
    label: 'About',
    path: '/about',
    children: [
      { label: 'About us', path: '/about' },
      { label: 'Our team', path: '/team' },
    ],
  },
  { label: 'Contact', path: '/contact' },
];

const COMPACT_AT = 120; // shrink after this many px…
const EXPAND_AT = 40; // …and only grow again near the top, so it can't flicker on one threshold

function Navigation() {
  const [compact, setCompact] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setCompact((prev) => (prev ? y > EXPAND_AT : y > COMPACT_AT));
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    // Close any hover/focus dropdown after navigating
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!location.hash) window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', mobileOpen);
    return () => document.body.classList.remove('menu-open');
  }, [mobileOpen]);

  const isActive = (item) =>
    location.pathname === item.path ||
    (item.children || []).some((c) => !c.all && c.path !== '/' && location.pathname === c.path);

  return (
    <>
      <header className={`site-header${compact ? ' is-compact' : ''}`}>
        <div className="topbar" data-track-location="topbar">
          <div className="container topbar-inner">
            <a href={company.mapsUrl} target="_blank" rel="noopener noreferrer">
              <Icon name="pin" size={14} /> {company.address.street}, {company.address.locality}
            </a>
            <span className="topbar-right">
              <span>
                <Icon name="clock" size={14} /> Mon–Thu {company.hours.weekdays} · Fri {company.hours.friday}
              </span>
              <a href={salesPhone.href}>
                <Icon name="phone" size={14} /> <strong>{salesPhone.number}</strong>
              </a>
              <a href={`mailto:${company.email}`}>
                <Icon name="mail" size={14} /> {company.email}
              </a>
            </span>
          </div>
        </div>

        <nav className="mainnav" aria-label="Main" data-track-location="header">
          <div className="container mainnav-inner">
            <Link to="/" className="brand" aria-label={`${company.name} — home`}>
              <img src="/logos/ojanna-logo-blue.svg" alt="OJANNA" width="164" height="62" />
              <span className="brand-sub">Ceiling &amp; Drywall Distributors</span>
            </Link>

            <ul className="menu">
              {MENU.map((item) => (
                <li key={item.label} className={item.children ? 'has-dropdown' : undefined}>
                  <Link to={item.path} className={`menu-link${isActive(item) ? ' active' : ''}`}>
                    {item.label}
                    {item.children && <Icon name="chevron" size={14} className="menu-chevron" />}
                  </Link>
                  {item.children && (
                    <div className={`dropdown${item.wide || item.children.length > 6 ? ' dropdown-wide' : ''}`}>
                      <ul>
                        {item.children.map((child) => (
                          <li key={child.label} className={child.all ? 'dropdown-all' : undefined}>
                            <Link to={child.path} className={child.logo ? 'has-logo' : undefined}>
                              {child.logo && (
                                <span className="dropdown-logo">
                                  <img src={child.logo} alt="" loading="lazy" />
                                </span>
                              )}
                              <span className="dropdown-text">
                                <span>{child.label}</span>
                                {child.note && <small>{child.note}</small>}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="mainnav-actions">
              <a
                href={salesPhone.href}
                className="mainnav-phone"
              >
                <Icon name="phone" size={18} /> {salesPhone.number}
              </a>
              <Link to={quotePath} className="btn btn-primary mainnav-quote">
                Get a quote <Icon name="arrow" size={16} />
              </Link>
              {/* Phones: business hours take the quote button's place (quote lives in the tab bar) */}
              <a
                href={company.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mainnav-hours"
                aria-label={`Business hours: ${hoursLines.join(', ')}. Open in Google Maps`}
              >
                <Icon name="clock" size={16} />
                <span>
                  {hoursLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </a>
            </div>
          </div>
        </nav>

      </header>

      {/* Mobile: app-style bottom tab bar + slide-up menu sheet */}
      <div className="mobile-backdrop" hidden={!mobileOpen} onClick={() => setMobileOpen(false)} />
      <div
        id="mobile-menu"
        data-track-location="menu"
        className={`mobile-sheet${mobileOpen ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-sheet-head">
          <img src="/logos/ojanna-logo-blue.svg" alt="" width="106" height="40" />
          <span>Ceiling &amp; Drywall Distributors</span>
        </div>
        {MENU.map((item) => (
          <div key={item.label} className="mobile-group">
            <NavLink to={item.path} end className="mobile-group-title" tabIndex={mobileOpen ? undefined : -1}>
              {item.label}
              <Icon name="arrow" size={16} />
            </NavLink>
            {item.children && (
              <div className="mobile-links">
                {item.children
                  .filter((c) => !c.all)
                  .map((child) => (
                    <Link key={child.label} to={child.path} tabIndex={mobileOpen ? undefined : -1}>
                      {child.label}
                    </Link>
                  ))}
              </div>
            )}
          </div>
        ))}
        <a
          href={company.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mobile-visit"
          tabIndex={mobileOpen ? undefined : -1}
        >
          <span className="mobile-visit-row">
            <Icon name="clock" size={18} />
            <span>
              <strong>Business hours</strong>
              Mon–Thu {company.hours.weekdays} · Fri {company.hours.friday}
            </span>
          </span>
          <span className="mobile-visit-row">
            <Icon name="pin" size={18} />
            <span>
              <strong>Visit us</strong>
              {company.address.line1}, {company.address.line2}, {company.address.locality}
            </span>
          </span>
          <span className="mobile-visit-cta">
            Open in Google Maps <Icon name="arrow" size={16} />
          </span>
        </a>
      </div>

      <nav className="mobile-tabbar" aria-label="Quick actions" data-track-location="tabbar">
        <a href={salesPhone.href}>
          <Icon name="phone" size={22} />
          <span>Call</span>
        </a>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="whatsapp" size={22} />
          <span>WhatsApp</span>
        </a>
        <Link to={quotePath} className="mobile-tab-quote">
          <Icon name="mail" size={22} />
          <span>Quote</span>
        </Link>
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="directions" size={22} />
          <span>Directions</span>
        </a>
        <button
          type="button"
          className={mobileOpen ? 'is-active' : undefined}
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          <Icon name={mobileOpen ? 'close' : 'menu'} size={22} />
          <span>{mobileOpen ? 'Close' : 'Menu'}</span>
        </button>
      </nav>
    </>
  );
}

export default Navigation;
